import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Phone, MapPin, Clock, Award, Heart, Star } from 'lucide-react';
import { getDynamicPhoneNumber } from '../../services/constants';

const Footer = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [fallbackPhone, setFallbackPhone] = useState('+19162563468');

  // Check if we're on the homepage
  const isHomePage = location?.pathname === '/';

  // Set up dynamic fallback phone number
  useEffect(() => {
    const updateFallbackPhone = async () => {
      try {
        const dynamicPhone = await getDynamicPhoneNumber();
        setFallbackPhone(dynamicPhone);
      } catch (error) {
        setFallbackPhone('+19162563468');
      }
    };

    updateFallbackPhone();

    const handleCurrentAgentChange = () => {
      updateFallbackPhone();
    };

    window.addEventListener('currentAgentChanged', handleCurrentAgentChange);

    return () => {
      window.removeEventListener('currentAgentChanged', handleCurrentAgentChange);
    };
  }, []);

  const formatPhoneDisplay = (phone) => {
    return phone?.replace('+1', '')?.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');
  };

  // Enhanced navigation handler with aggressive click event handling
  const handleLogoClick = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    console.log('🚀 Footer logo clicked on homepage - navigating to authentication portal');
    navigate('/authentication-login-portal');
  };

  const OFFICE_ADDRESS = '901 H Street, Suite 103, Sacramento, CA 95814';

  // Navigation items matching Header component exactly
  const navigationItems = [
    { name: 'Home', href: '/' },
    { name: 'About Us', href: '/about-us' },
    { name: 'Services', href: '/services' },
    { name: 'Locations', href: '/locations' },
    { name: 'Contact', href: '/contact-us' },
    { name: 'Blog', href: '/blog' },
    { name: 'Inmate Search', href: '/inmate-search-widget' }
  ];

  // Legal documents links
  const legalLinks = [
    { name: 'Terms of Service', href: '/terms-of-service' },
    { name: 'Privacy Policy', href: '/privacy-policy' },
    { name: 'Legal Disclaimer', href: '/legal-disclaimer-and-compliance' }
  ];

  return (
    <footer className="bg-gray-900 text-white py-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="grid md:grid-cols-4 gap-12 mb-12">
          {/* Company Info */}
          <div className="space-y-4">
            <div className="flex flex-col items-start space-y-3">
              {/* Conditionally render clickable logo ONLY on homepage "/" */}
              {isHomePage ? (
                <button
                  onClick={handleLogoClick}
                  type="button"
                  className="block w-36 h-36 group cursor-pointer transition-all hover:opacity-90 focus:outline-none focus:ring-4 focus:ring-blue-500 focus:ring-opacity-50 rounded-lg"
                  aria-label="Navigate to authentication portal - homepage logo"
                  style={{ 
                    position: 'relative',
                    zIndex: 9999,
                    pointerEvents: 'auto',
                    cursor: 'pointer',
                    touchAction: 'manipulation'
                  }}
                >
                  <img 
                    src="/assets/images/162111a8-97b7-4fd6-a950-171d24f8aaac-1764544825148.png" 
                    alt="Sailors Bail Bonds - Professional nautical-themed logo featuring anchor with rope in teal and gold - Click to access authentication portal"
                    className="w-36 h-36 object-contain group-hover:scale-110 transition-transform duration-300 pointer-events-none"
                    style={{ 
                      width: '144px', 
                      height: '144px', 
                      minWidth: '144px', 
                      minHeight: '144px',
                      userSelect: 'none',
                      WebkitUserSelect: 'none',
                      MozUserSelect: 'none',
                      msUserSelect: 'none'
                    }}
                    draggable="false"
                  />
                </button>
              ) : (
                <div className="block w-36 h-36 opacity-100">
                  <img 
                    src="/assets/images/162111a8-97b7-4fd6-a950-171d24f8aaac-1764544825148.png" 
                    alt="Sailors Bail Bonds - Professional nautical-themed logo featuring anchor with rope in teal and gold"
                    className="w-36 h-36 object-contain pointer-events-none"
                    style={{ 
                      width: '144px', 
                      height: '144px', 
                      minWidth: '144px', 
                      minHeight: '144px',
                      cursor: 'default',
                      userSelect: 'none'
                    }}
                    draggable="false"
                  />
                </div>
              )}
              <h4 className="text-2xl font-black" style={{ fontFamily: 'Roboto Slab, serif' }}>
                Sailors Bail Bonds
              </h4>
            </div>
            <p className="text-gray-400 leading-relaxed" style={{ fontFamily: 'Open Sans, sans-serif' }}>
              In bail bonds business since 2009, serving all California counties with compassionate, professional service.
            </p>
          </div>

          {/* Contact */}
          <div className="space-y-4">
            <h5 className="text-xl font-bold mb-4" style={{ fontFamily: 'Roboto Slab, serif' }}>
              Contact Us
            </h5>
            <div className="space-y-3">
              <a 
                href={`tel:${fallbackPhone}`}
                className="flex items-center space-x-2 text-gray-300 hover:text-green-500 transition-colors text-base font-semibold"
                style={{ fontFamily: 'Open Sans, sans-serif' }}
              >
                <Phone className="w-5 h-5" />
                <span>{formatPhoneDisplay(fallbackPhone)}</span>
              </a>
              <div className="flex items-start space-x-2 text-gray-300 text-base" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                <MapPin className="w-5 h-5 mt-1 flex-shrink-0" />
                <span>{OFFICE_ADDRESS}</span>
              </div>
              <div className="flex items-center space-x-2 text-gray-300 text-base" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                <Clock className="w-5 h-5" />
                <span>24/7 - Always Available</span>
              </div>
            </div>
          </div>

          {/* Quick Links - Navigation Tabs Matching Header */}
          <div className="space-y-4">
            <h5 className="text-xl font-bold mb-4" style={{ fontFamily: 'Roboto Slab, serif' }}>
              Quick Links
            </h5>
            <nav className="flex flex-col space-y-2" aria-label="Footer navigation">
              {navigationItems?.map((item) => (
                <Link 
                  key={item?.name}
                  to={item?.href} 
                  className="text-gray-300 hover:text-blue-500 transition-colors text-base font-semibold"
                  style={{ fontFamily: 'Open Sans, sans-serif' }}
                >
                  {item?.name}
                </Link>
              ))}
            </nav>
          </div>

          {/* Trust Badges */}
          <div className="space-y-4">
            <h5 className="text-xl font-bold mb-4" style={{ fontFamily: 'Roboto Slab, serif' }}>
              Why Trust Us
            </h5>
            <div className="space-y-3">
              <div className="flex items-center space-x-2 text-gray-300 text-base" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                <Award className="w-5 h-5 text-blue-500" />
                <span>Licensed & Insured</span>
              </div>
              <div className="flex items-center space-x-2 text-gray-300 text-base" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                <Heart className="w-5 h-5 text-green-500" />
                <span>Family Owned</span>
              </div>
              <div className="flex items-center space-x-2 text-gray-300 text-base" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                <Star className="w-5 h-5 text-amber-400" />
                <span>Highly Rated</span>
              </div>
            </div>
          </div>
        </div>

        {/* Legal Links Section - NEW */}
        <div className="border-t border-gray-800 pt-8 pb-8">
          <div className="flex flex-wrap justify-center gap-6 mb-4">
            {legalLinks?.map((link) => (
              <Link
                key={link?.name}
                to={link?.href}
                className="text-gray-400 hover:text-blue-400 transition-colors text-sm font-semibold underline"
                style={{ fontFamily: 'Open Sans, sans-serif' }}
              >
                {link?.name}
              </Link>
            ))}
            {/* Discreet Admin Login Portal Link */}
            <Link
              to="/authentication-login-portal"
              className="text-gray-700 hover:text-gray-500 transition-colors text-xs opacity-30 hover:opacity-60"
              style={{ fontFamily: 'Open Sans, sans-serif' }}
              aria-label="Administrator access portal"
            >
              •
            </Link>
          </div>
        </div>

        {/* Copyright */}
        <div className="border-t border-gray-800 pt-8 text-center">
          <p className="text-gray-400 text-base" style={{ fontFamily: 'Open Sans, sans-serif' }}>
            © {new Date()?.getFullYear()} Sailors Bail Bonds. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;